import { useState, useEffect } from "react"
import axios from "axios"
import { toast } from "react-toastify"
import Select from "react-select"
import { DataGrid } from "@mui/x-data-grid"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

function AddPurchaseSettlement() {
  const [suppliers, setSuppliers] = useState([])
  const [selectedSupplier, setSelectedSupplier] = useState(null) // Store selected supplier
  const [selectedSupplierName, setSelectedSupplierName] = useState(null) // Stores supplier name
  const [bills, setBills] = useState([]) // Purchase Bills
  const [supplierPayments, setSupplierPayments] = useState([]) // Payments made to suppliers
  const [debitNotes, setDebitNotes] = useState([]) // Debit notes from suppliers
  const [selectedBills, setSelectedBills] = useState([]) // For tracking selected bills
  const [selectedSupplierPayments, setSelectedSupplierPayments] = useState([]) // For tracking selected payments
  const [selectedDebitNotes, setSelectedDebitNotes] = useState([]) // For tracking selected debit notes
  const [totalSettlementAmount, setTotalSettlementAmount] = useState(0) // Total from payments + debit notes
  const [totalBillAmount, setTotalBillAmount] = useState(0) // Total from selected bills
  const [userRole, setUserRole] = useState("") // State to store user role
  const [user, setUser] = useState(null) // State to store the full user object
  const [organizations, setOrganizations] = useState([]) // List of organizations for Main Admin

  useEffect(() => {
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token")
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.")
          return
        }

        // Fetch user data
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        
        // --- THIS IS THE CHANGE ---
        setUser(userResponse.data) // Store the entire user object
        setUserRole(userResponse.data.role)
        // --- END CHANGE ---

        // Fetch organizations if the user is a Main Admin
        if (userResponse.data.role === "Main Admin") {
          // ... (your existing org-fetching logic is fine)
        }
      } catch (error) {
        console.error("Error fetching user data:", error)
        toast.error("Error fetching user data")
      }
    }

    fetchSuppliers()
    fetchUserRoleAndOrganizations()
  }, [])

  useEffect(() => {
    if (selectedSupplier) {
      getBills()
    }
  }, [selectedSupplier])

  useEffect(() => {
    if (selectedSupplier) {
      getSupplierPayments()
    }
  }, [selectedSupplier])

  useEffect(() => {
    if (selectedSupplier) {
      getDebitNotes()
    }
  }, [selectedSupplier])

  useEffect(() => {
    // Clear all selected items when supplier changes
    if (selectedSupplier) {
      setSelectedBills([])
      setSelectedSupplierPayments([])
      setSelectedDebitNotes([])
      setTotalSettlementAmount(0)
      setTotalBillAmount(0)
    }
  }, [selectedSupplier])

  useEffect(() => {
    const total = selectedBills.reduce((acc, curr) => acc + Number.parseFloat(curr.balance), 0)
    setTotalBillAmount(total)
  }, [selectedBills])

  useEffect(() => {
    const paymentTotal = selectedSupplierPayments.reduce((acc, curr) => {
      const amountToUse = Number.parseFloat(curr.approved_amount) || 0
      return acc + amountToUse
    }, 0)

    const debitNoteTotal = selectedDebitNotes.reduce((acc, curr) => {
      const amountToUse = Number.parseFloat(curr.approved_amount) || 0
      return acc + amountToUse
    }, 0)

    const total = paymentTotal + debitNoteTotal

    if (total > totalBillAmount) {
      toast.error("Total payments and debit notes cannot exceed the total bill amount.")
      setTotalSettlementAmount(totalBillAmount)
    } else {
      setTotalSettlementAmount(total)
    }
  }, [selectedSupplierPayments, selectedDebitNotes, totalBillAmount])

  const getBills = async () => {
    if (!selectedSupplierName) {
        setBills(null)
        return
    }

    try {
        const token = localStorage.getItem("token")
        if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
        }

        const response = await axios.get(
        `${API_BASE_URL}/grnInvoices/getInvoiceBySupplier/${selectedSupplierName}`, // use name
        {
            headers: {
            Authorization: `Bearer ${token}`,
            },
        }
        )

        if (response.status === 200 && response.data.length > 0) {
        setBills(response.data)
        } else {
        setBills(null)
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
        setBills(null)
        toast.info("No bills found for this supplier.")
        } else {
        toast.error("Error fetching bills")
        }
        console.error("Error fetching bills:", error.response || error)
    }
    }

  const getSupplierPayments = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/supplierPayments/getBySupplier/${selectedSupplier}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      if (response.status === 200 && response.data.length > 0) {
        // Filter out canceled payments
        const activePayments = response.data.filter(
          (payment) => (payment.status || "").toLowerCase().trim() !== "canceled"
        )
        setSupplierPayments(activePayments.length > 0 ? activePayments : null)
      } else {
        setSupplierPayments(null) // No payments
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setSupplierPayments(null)
        toast.info("No supplier payments found for this supplier.")
      } else {
        toast.error("Error fetching supplier payments")
      }
      console.error("Error fetching supplier payments:", error.response || error)
    }
  }

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      // Assuming an endpoint like this exists
      const response = await axios.get(`${API_BASE_URL}/suppliers/get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setSuppliers(response.data.suppliers) // Assuming response structure
    } catch (error) {
      console.error("Error fetching suppliers:", error)
      toast.error("Error fetching suppliers")
    }
  }

  const getDebitNotes = async () => {
    try {
        const token = localStorage.getItem("token")
        if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
        }

        const response = await axios.get(`${API_BASE_URL}/debitNotes/getDebitNoteByCustomer/${selectedSupplier}`, 
        { headers: { Authorization: `Bearer ${token}` } }
        )

        if (response.status === 200 && response.data.length > 0) {
        setDebitNotes(response.data)
        } else {
        setDebitNotes(null)
        toast.info("No debit notes found for this supplier.")
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
        setDebitNotes(null)
        toast.info("No debit notes found for this supplier.")
        } else {
        toast.error("Error fetching debit notes")
        }
        console.error("Error fetching debit notes:", error.response || error)
    }
    }

  const customSupplierIDSelect = (supplierOptions, selectedSupplier, setSelectedSupplier, setSelectedSupplierName) => {
    return (
      <Select
        options={supplierOptions}
        getOptionLabel={(e) => e.supplier_code} // Show supplier ID
        getOptionValue={(e) => e._id} // Use _id as value
        isSearchable={true}
        placeholder="Search by Supplier ID"
        className="w-full"
        value={supplierOptions.find((option) => option._id === selectedSupplier) || null}
        onChange={(selectedOption) => {
          setSelectedSupplier(selectedOption?._id) // Set selected supplier ID
          setSelectedSupplierName(selectedOption?.supplier_name) // Auto-fill name
        }}
      />
    )
  }

  const customSupplierNameSelect = (
    supplierOptions,
    selectedSupplierName,
    setSelectedSupplierName,
    setSelectedSupplier,
  ) => {
    return (
      <Select
        options={supplierOptions}
        getOptionLabel={(e) => e.supplier_name} // Show supplier name
        getOptionValue={(e) => e._id} // Use _id as value
        isSearchable={true}
        placeholder="Search by Supplier Name"
        className="w-full"
        value={supplierOptions.find((option) => option.supplier_name === selectedSupplierName) || null}
        onChange={(selectedOption) => {
          setSelectedSupplier(selectedOption?._id) // Auto-fill supplier ID
          setSelectedSupplierName(selectedOption?.supplier_name) // Set selected name
        }}
      />
    )
  }

  const handleAddBill = (bill) => {
    // Check if there is already a bill selected
    if (selectedBills.length > 0) {
      toast.error("You can only add one bill at a time for settlement.")
      return
    }
    setSelectedBills([bill])
    // Remove from available bills list
    setBills((currentBills) => currentBills.filter((b) => b.bill_id !== bill.bill_id)) // Assuming bill_id
  }

  const handleAddDebitNote = (debitNote) => {
    setSelectedDebitNotes((currentDebitNotes) => [...currentDebitNotes, debitNote])
    setDebitNotes((currentDebitNotes) => currentDebitNotes.filter((dn) => dn._id !== debitNote._id))
  }

  const handleAddSupplierPayment = (payment) => {
    // Normalize status for safe comparison
    const status = (payment.status || "").toString().trim().toLowerCase()

    if (status === "canceled") {
      toast.error("Cannot add a canceled payment receipt.")
      return
    }

    setSelectedSupplierPayments((currentPayments) => [...currentPayments, payment])
    setSupplierPayments((currentPayments) =>
      currentPayments.filter((pay) => pay.supplier_payment_id !== payment.supplier_payment_id),
    )
  }

  const handleRemoveSelectedDebitNote = (debitNote) => {
    // Add back to the list
    setDebitNotes((currentDebitNotes) => [...currentDebitNotes, debitNote])
    // Remove from selected
    setSelectedDebitNotes((currentDebitNotes) => currentDebitNotes.filter((dn) => dn._id !== debitNote._id))
  }

  const handleRemoveSelectedBill = (bill) => {
    // Add back to the list
    setBills((currentBills) => [...currentBills, bill])
    // Clear the selected bills
    setSelectedBills([])
  }

  const handleRemoveSupplierPayment = (supplier_payment_id) => {
    // Find the payment being removed
    const paymentToRemove = selectedSupplierPayments.find(
      (pay) => pay.supplier_payment_id === supplier_payment_id,
    )
    // Add it back to the main list
    if (paymentToRemove) {
      setSupplierPayments((currentPayments) => [...currentPayments, paymentToRemove])
    }
    // Remove from selected list
    setSelectedSupplierPayments((currentPayments) =>
      currentPayments.filter((pay) => pay.supplier_payment_id !== supplier_payment_id),
    )
  }

  const handleSupplierPaymentAmountChange = (event, payment) => {
    const newAmount = Number.parseFloat(event.target.value) || 0

    // Check against available balance
    if (newAmount > payment.balance) { // Assuming 'balance'
      toast.error(`Amount cannot exceed available payment balance of ${payment.balance}`)
      event.target.value = payment.approved_amount || 0
      return
    }

    // Calculate potential new total
    const paymentTotal = selectedSupplierPayments.reduce((acc, curr) => {
      if (curr.supplier_payment_id === payment.supplier_payment_id) {
        return acc + newAmount
      } else {
        return acc + (Number.parseFloat(curr.approved_amount) || 0)
      }
    }, 0)

    const debitNoteTotal = selectedDebitNotes.reduce((acc, curr) => {
      return acc + (Number.parseFloat(curr.approved_amount) || 0)
    }, 0)

    const potentialNewTotal = paymentTotal + debitNoteTotal

    if (potentialNewTotal > totalBillAmount) {
      toast.error("Total payments and debit notes cannot exceed the total bill amount.")
      event.target.value = payment.approved_amount
      return
    }

    const updatedPayments = selectedSupplierPayments.map((p) => {
      if (p.supplier_payment_id === payment.supplier_payment_id) {
        return { ...p, approved_amount: newAmount }
      }
      return p
    })
    setSelectedSupplierPayments(updatedPayments)
  }

  const handleDebitNoteAmountChange = (event, debitNote) => {
    const newAmount = Number.parseFloat(event.target.value) || 0

    // Check against available balance
    if (newAmount > debitNote.balance) {
      toast.error(`Amount cannot exceed available debit note balance of ${debitNote.balance}`)
      event.target.value = debitNote.approved_amount || debitNote.balance
      return
    }

    // Calculate potential new total
    const paymentTotal = selectedSupplierPayments.reduce((acc, curr) => {
      return acc + (Number.parseFloat(curr.approved_amount) || 0)
    }, 0)

    const debitNoteTotal = selectedDebitNotes.reduce((acc, curr) => {
      if (curr._id === debitNote._id) {
        return acc + newAmount
      } else {
        return acc + (Number.parseFloat(curr.approved_amount) || 0)
      }
    }, 0)

    const potentialNewTotal = paymentTotal + debitNoteTotal

    if (potentialNewTotal > totalBillAmount) {
      toast.error("Total payments and debit notes cannot exceed the total bill amount.")
      event.target.value = debitNote.approved_amount || ""
      return
    }

    const updatedDebitNotes = selectedDebitNotes.map((dn) => {
      if (dn._id === debitNote._id) {
        return { ...dn, approved_amount: newAmount }
      }
      return dn
    })
    setSelectedDebitNotes(updatedDebitNotes)
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      // --- NEW VALIDATIONS ---
      if (!user || !user._id || !user.organization) {
        toast.error("User data not loaded. Please refresh and try again.");
        return;
      }
      if (!selectedSupplier) {
        toast.error("Please select a supplier.");
        return;
      }
      // --- END NEW VALIDATIONS ---

      if (selectedBills.length === 0) {
        toast.error("Please select a purchase invoice to settle.");
        return;
      }

      if (totalSettlementAmount <= 0) {
        toast.error("Settlement amount must be greater than zero.");
        return;
      }

      // This check is from your old code and is still a good idea
      if (totalSettlementAmount > totalBillAmount + 0.01) { // Added 0.01 for float precision
        toast.error("Settlement amount cannot exceed the total bill amount.");
        return;
      }

      // --- START: CONSTRUCT THE CORRECT PAYLOAD ---

      // 1. Format Invoices
      // The backend expects an array of invoices with the amount applied to each.
      // Since your UI only allows one bill, the amount applied is the total settlement.
      const invoices = selectedBills.map((bill) => ({
        invoice_id: bill._id, // Use the invoice's MongoDB _id
        applied_amount: totalSettlementAmount, // The total amount being paid
      }));

      // 2. Format Payment Receipts
      // Your controller expects 'payment_receipt_id' and 'amount_applied'
      const payment_receipts = selectedSupplierPayments.map((p) => ({
        payment_receipt_id: p._id, // The payment receipt's MongoDB _id
        amount_applied: p.approved_amount, // The amount from the input
      }));

      // 3. Format Debit Notes
      // Your controller expects 'debit_note_id' and 'debited_amount'
      const debit_notes = selectedDebitNotes.map((dn) => ({
        debit_note_id: dn._id, // The debit note's MongoDB _id
        debited_amount: dn.approved_amount, // The amount from the input
      }));

      // 4. Build the final payload
      const payload = {
        supplier_id: selectedSupplier,
        organization: user.organization, // From the logged-in user
        createdBy: user._id,           // From the logged-in user
        invoices: invoices,
        payment_receipts: payment_receipts,
        debit_notes: debit_notes,
        // DO NOT send 'settledAmount'. The backend calculates this.
      };
      
      // --- END: CONSTRUCT THE CORRECT PAYLOAD ---

      const response = await axios.post(
        `${API_BASE_URL}/purchaseSettlements/add`, // Your endpoint
        payload, // Send the new, correct payload
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Invoice settled successfully");
      
      // Reset your form
      setSelectedSupplier(null);
      setSelectedSupplierName(null);
      setBills([]);
      setSupplierPayments([]);
      setDebitNotes([]);
      setSelectedBills([]);
      setSelectedSupplierPayments([]);
      setSelectedDebitNotes([]);
      setTotalBillAmount(0);
      setTotalSettlementAmount(0);
      setUser(null); // Re-fetch user on next load if needed, or just clear form
      
    } catch (error) {
      console.error("Error settling Invoice:", error);
      toast.error(error.response?.data?.message || "Error settling Invoice");
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Purchase Settlement</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700">Select Supplier ID:</label>
            {customSupplierIDSelect(suppliers, selectedSupplier, setSelectedSupplier, setSelectedSupplierName)}
          </div>
          <div>
            <label className="block text-gray-700">Select Supplier Name:</label>
            {customSupplierNameSelect(suppliers, selectedSupplierName, setSelectedSupplierName, setSelectedSupplier)}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold">Purchasing Invoices</h3>
            <DataGrid
            rows={bills || []} // ensure it's an array
            getRowId={(row) => row._id || row.bill_id} // fallback to bill_id if _id missing
            columns={[
                { field: "grn_code", headerName: "Invoice Code", flex: 1.5 },
                { field: "amount", headerName: "Amount", flex: 0.8 },
                { field: "balance", headerName: "Balance", flex: 0.8 },
                {
                field: "status",
                headerName: "Status",
                flex: 0.8,
                renderCell: (params) => {
                    const statusClassMap = {
                    Opend: "bg-lime-600 text-white",
                    "Partially Settled": "bg-blue-500 text-white",
                    "Fully Settled": "bg-green-500 text-white",
                    }
                    return <span className={`px-2 py-1 rounded-sm ${statusClassMap[params.row.status] || "bg-gray-500 text-white"}`}>{params.row.status}</span>
                },
                },
                {
                field: "action",
                headerName: "Action",
                flex: 0.8,
                renderCell: (params) => (
                    <button
                    className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                    onClick={() => handleAddBill(params.row)}
                    >
                    Add
                    </button>
                ),
                },
            ]}
            />

          </div>
          <div>
            <h3 className="text-xl font-semibold">Debit Notes</h3>
            <DataGrid
              rows={debitNotes}
              getRowId={(row) => row._id}
              columns={[
                { field: "debit_note_code", headerName: "Debit Note ID", flex: 1.5 }, // Assuming debit_note_code
                { field: "total_debit_amount", headerName: "Amount", flex: 0.8 }, // Assuming total_debit_amount
                { field: "balance", headerName: "Balance", flex: 0.8 },
                {
                  field: "status",
                  headerName: "Status",
                  flex: 1,
                  renderCell: (params) => {
                    const status = params.row.status
                    const statusClassMap = {
                      Pending: "bg-lime-600 text-white",
                      "Partially Settled": "bg-blue-500 text-white",
                      "Fully Settled": "bg-green-500 text-white",
                    }
                    const statusClass = statusClassMap[status] || "bg-gray-500 text-white"
                    return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>
                  },
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 0.8,
                  renderCell: (params) => (
                    <button
                      className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                      onClick={() => handleAddDebitNote(params.row)}
                    >
                      Add
                    </button>
                  ),
                },
              ]}
            />
          </div>
          <div>
            <h3 className="text-xl font-semibold">Supplier Payments</h3>
            <DataGrid
              rows={supplierPayments}
              getRowId={(row) => row.supplier_payment_id} // Assuming supplier_payment_id
              columns={[
                { field: "supplier_payment_id", headerName: "Payment ID", flex: 1.5 },
                { field: "amount", headerName: "Amount", flex: 0.8 },
                { field: "balance", headerName: "Balance", flex: 0.8 },
                {
                  field: "status",
                  headerName: "Status",
                  flex: 1,
                  renderCell: (params) => {
                    const status = params.row.status
                    const statusClassMap = {
                      Pending: "bg-lime-600 text-white",
                      "Partially Utilized": "bg-blue-500 text-white",
                      "Fully Settled": "bg-green-500 text-white",
                    }
                    const statusClass = statusClassMap[status] || "bg-gray-500 text-white"
                    return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>
                  },
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 0.8,
                  renderCell: (params) => (
                    <button
                      className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                      onClick={() => handleAddSupplierPayment(params.row)}
                    >
                      Add
                    </button>
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-6">
          <div className="border-2 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Selected Purchasing Invoices</h3>
            <div className="grid grid-cols-4 text-center mb-2 font-semibold">
              <div>Invoice Code</div>
              <div>Amount</div>
              <div>Balance</div>
              <div>Action</div>
            </div>
            <ul className="border p-4 rounded-lg mt-4">
              {selectedBills.map((bill) => (
                <li key={bill.grn_code} className="flex justify-between items-center mb-2 border-b pb-2">
                  <div>{bill.grn_code}</div>
                  <div>{bill.amount.toFixed(2)}</div>
                  <div>{bill.balance.toFixed(2)}</div>
                  <div>{}</div>
                  <button
                    className="px-3 py-1 rounded bg-red-500 text-white text-xs"
                    onClick={() => handleRemoveSelectedBill(bill)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="text-left mt-6">
              <h3 className="text-xl font-bold text-black mb-6">
                Total Bill Amount (LKR): {totalBillAmount.toFixed(2)}
              </h3>
            </div>
          </div>
          <div className="border-2 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Selected Supplier Payments / Debit Notes</h3>
            <div className="grid grid-cols-5 text-center mb-2 font-semibold">
              <div>ID</div>
              <div>Amount</div>
              <div>Balance</div>
              <div>Approved Amount</div>
              <div>Action</div>
            </div>

            <ul className="border p-4 rounded-lg mt-4">
              {/* Render selected supplier payments */}
              {selectedSupplierPayments.map((payment) => (
                <li
                  key={`payment-${payment.supplier_payment_id}`}
                  className="grid grid-cols-5 items-center text-center mb-2 border-b pb-2"
                >
                  <div>{payment.supplier_payment_id}</div>
                  <div>{payment.amount.toFixed(2)}</div>
                  <div>{payment.balance.toFixed(2)}</div>
                  <div>
                    <input
                      type="number"
                      value={payment.approved_amount || ""}
                      onChange={(e) => handleSupplierPaymentAmountChange(e, payment)}
                      className="w-32 p-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter amount"
                    />
                  </div>
                  <div>
                    <button
                      className="px-2 py-1 rounded bg-red-500 text-white text-xs"
                      onClick={() => handleRemoveSupplierPayment(payment.supplier_payment_id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}

              {/* Render selected debit notes */}
              {selectedDebitNotes.map((debitNote) => (
                <li key={`debit-${debitNote._id}`} className="grid grid-cols-5 items-center text-center mb-2 border-b pb-2">
                  <div>{debitNote.debit_note_code}</div>
                  <div>{debitNote.total_debit_amount.toFixed(2)}</div>
                  <div>{debitNote.balance.toFixed(2)}</div>
                  <div>
                    <input
                      type="number"
                      value={debitNote.approved_amount || ""}
                      onChange={(e) => handleDebitNoteAmountChange(e, debitNote)}
                      className="w-32 p-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter amount"
                    />
                  </div>
                  <div>
                    <button
                      className="px-2 py-1 rounded bg-red-500 text-white text-xs"
                      onClick={() => handleRemoveSelectedDebitNote(debitNote)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="text-left mt-6">
              <h3 className="text-xl font-bold text-black mb-6">
                Total Settlement Amount (LKR): {totalSettlementAmount.toFixed(2)}
              </h3>
            </div>
          </div>
        </div>
        <div className="text-left mt-6">
          <h3 className="text-xl font-bold text-black mb-6">
            Remaining Amount to Settle (LKR): {(totalBillAmount - totalSettlementAmount).toFixed(2)}
          </h3>
          {totalBillAmount > 0 && totalBillAmount - totalSettlementAmount === 0 && (
            <h3 className="text-xl font-bold text-green-500">Invoice is fully settled!</h3>
          )}
        </div>
        <div className="text-right mt-6">
          <button onClick={handleSubmit} className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-blue-600">
            Settle Invoice
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddPurchaseSettlement